/** @jsxImportSource theme-ui */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import type { LocationStateType } from '../types/navigation';
import {
  Spinner,
  Text,
  Box,
  PlaceholderMessage,
  Flex,
} from '@infotrack/zenith-ui';
import { useTopFiveData } from '../hooks/useTopFiveData';
import { TopFiveCard } from '../components/topFiveCard';
import { TopFiveHeader } from '../components/topFiveHeader';
import { FilterType } from '../constants/topFiveOptions';
import {
  pageStyles,
  contentStyles,
  listStyles,
  emptyStyles,
} from './topFivePage.styles';

export const TopFivePage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedPeriod, setSelectedPeriod] = useState<'current' | 'previous'>(
    (searchParams.get('period') as 'current' | 'previous') || 'current'
  );
  const [searchText, setSearchText] = useState<string>(
    searchParams.get('search') || ''
  );
  const [searchType, setSearchType] = useState<FilterType>(
    (searchParams.get('type') as FilterType) || '0'
  );
  const [shouldSearch, setShouldSearch] = useState<boolean>(
    searchParams.has('search') && searchParams.get('search')!.trim().length >= 3
  );

  const { topFiveData, isLoading, error, refetch } = useTopFiveData({
    searchText,
    searchType,
    enabled:
      (shouldSearch && searchText.trim().length >= 3) ||
      searchText.trim().length === 0, // Only enabled for valid search or initial load
  });

  const displayData = useMemo(() => {
    return selectedPeriod === 'current'
      ? topFiveData.currentPeriod
      : topFiveData.previousPeriod;
  }, [topFiveData, selectedPeriod]);

  const updateSearchParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);
        Object.entries(updates).forEach(([key, value]) => {
          if (value === null || value === '' || value === '0') {
            newParams.delete(key);
          } else {
            newParams.set(key, value);
          }
        });
        return newParams;
      });
    },
    [setSearchParams]
  );

  const handlePeriodChange = useCallback(
    (period: 'current' | 'previous') => {
      setSelectedPeriod(period);
      updateSearchParams({ period: period === 'current' ? null : period });
    },
    [updateSearchParams]
  );

  const handleSearch = useCallback(
    (text: string, type: FilterType) => {
      setSearchText(text);
      setSearchType(type);
      setShouldSearch(true);
      updateSearchParams({
        search: text.trim(),
        type: type === '0' ? null : type,
      });
    },
    [updateSearchParams]
  );

  const handleClearSearch = useCallback(() => {
    setSearchText('');
    setSearchType('0');
    setShouldSearch(false);
    updateSearchParams({
      search: null,
      type: null,
    });
  }, [updateSearchParams]);

  // Refetch data when navigating back from detail page
  const location: LocationStateType = useLocation();
  useEffect(() => {
    if (location.state?.fromDetail) {
      refetch();
    }
  }, [location, refetch]);

  if (isLoading) {
    return (
      <Box sx={pageStyles} role="status" aria-live="polite">
        <Spinner fullSize aria-label="Loading TOP 5 data" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={pageStyles} role="alert">
        <Text variant="medium" color="error">
          Failed to load TOP 5 data. Please try again later.
        </Text>
      </Box>
    );
  }

  return (
    <Flex sx={pageStyles} as="main">
      <TopFiveHeader
        resultCount={displayData.length}
        onPeriodChange={handlePeriodChange}
        selectedPeriod={selectedPeriod}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        searchText={searchText}
        searchType={searchType}
        onSearchTypeChange={setSearchType}
      />

      <Box sx={contentStyles} as="section" aria-label="TOP 5">
        {displayData.length === 0 ? (
          <Box sx={emptyStyles}>
            <PlaceholderMessage
              title="No TOP 5 available"
              subtitle={`No TOP 5 data available for the ${selectedPeriod} period.`}
            />
          </Box>
        ) : (
          <Flex sx={listStyles}>
            {displayData.map(staffTopFive => (
              <TopFiveCard
                key={staffTopFive.staff?.id}
                staffTopFive={staffTopFive}
                selectedPeriod={selectedPeriod}
              />
            ))}
          </Flex>
        )}
      </Box>
    </Flex>
  );
};
